import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import {
  LIST_CONTACT_SUBJECTS_QUERY,
  CREATE_CONTACT_SUBJECT_MUTATION,
  UPDATE_CONTACT_SUBJECT_MUTATION,
  DELETE_CONTACT_SUBJECT_MUTATION,
} from '../clients/gql';
import { dispatchMessage, dispatchException } from 'helper/snackbar';

interface ContactSubject {
  id: number;
  name: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface DialogState {
  open: boolean;
  editingSubject: ContactSubject | null;
}

const ContactSubjectsTab: React.FC = () => {
  const dispatch = useDispatch();
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    editingSubject: null,
  });
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Queries and mutations
  const { data, loading, refetch } = useQuery(LIST_CONTACT_SUBJECTS_QUERY, {
    variables: { where: {} }, // Show all subjects, including inactive ones
  });

  const [createSubjectMutation] = useMutation(CREATE_CONTACT_SUBJECT_MUTATION, {
    refetchQueries: ['ListContactSubjects'],
  });

  const [updateSubjectMutation] = useMutation(UPDATE_CONTACT_SUBJECT_MUTATION, {
    refetchQueries: ['ListContactSubjects'],
  });

  const [deleteSubjectMutation] = useMutation(DELETE_CONTACT_SUBJECT_MUTATION, {
    refetchQueries: ['ListContactSubjects'],
  });

  const subjects: ContactSubject[] = data?.listContactSubjects || [];

  const handleOpenDialog = (subject?: ContactSubject) => {
    setDialogState({
      open: true,
      editingSubject: subject || null,
    });
    setFormData({
      name: subject?.name || '',
      isActive: subject?.isActive ?? true,
    });
    setErrors({});
  };

  const handleCloseDialog = () => {
    setDialogState({ open: false, editingSubject: null });
    setFormData({ name: '', isActive: true });
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = String(i18next.t('contact-subjects-validation-name-required'));
    } else if (formData.name.trim().length < 3) {
      newErrors.name = String(i18next.t('contact-subjects-validation-name-min'));
    }

    // Check for duplicate names
    const isDuplicate = subjects.some(
      (subject) =>
        subject.name.toLowerCase() === formData.name.trim().toLowerCase() &&
        subject.id !== dialogState.editingSubject?.id
    );
    if (isDuplicate) {
      newErrors.name = String(i18next.t('contact-subjects-validation-name-duplicate'));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (dialogState.editingSubject) {
        await updateSubjectMutation({
          variables: {
            id: dialogState.editingSubject.id,
            data: {
              name: formData.name.trim(),
              isActive: formData.isActive,
            },
          },
        });
        dispatchMessage(dispatch, String(i18next.t('contact-subjects-updated')));
      } else {
        await createSubjectMutation({
          variables: {
            data: {
              name: formData.name.trim(),
              isActive: formData.isActive,
            },
          },
        });
        dispatchMessage(dispatch, String(i18next.t('contact-subjects-created')));
      }
      handleCloseDialog();
    } catch (error) {
      dispatchException(dispatch, error);
    }
  };

  const handleDelete = async (subject: ContactSubject) => {
    if (window.confirm(String(i18next.t('contact-subjects-deactivate-confirm', { name: subject.name })))) {
      try {
        await deleteSubjectMutation({
          variables: { id: subject.id },
        });
        dispatchMessage(dispatch, String(i18next.t('contact-subjects-deactivated')));
      } catch (error) {
        dispatchException(dispatch, error);
      }
    }
  };

  if (loading) {
    return <Box>Loading...</Box>;
  }

  return (
    <>
      <Helmet>
        <title>{String(i18next.t('contact-subjects-title'))}</title>
      </Helmet>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{i18next.t('contact-subjects-manage-title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {i18next.t('contact-subjects-add')}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        {i18next.t('contact-subjects-info')}
      </Alert>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{i18next.t('name')}</TableCell>
                  <TableCell align="center">{i18next.t('status')}</TableCell>
                  <TableCell align="center">{i18next.t('created-at')}</TableCell>
                  <TableCell align="center">{i18next.t('updated-at')}</TableCell>
                  <TableCell align="center">{i18next.t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...subjects]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((subject) => (
                    <TableRow key={subject.id}>
                      <TableCell>
                        <Typography variant="body2">{subject.name}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={subject.isActive ? String(i18next.t('active')) : String(i18next.t('inactive'))}
                          color={subject.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">
                          {subject.createdAt ? new Date(subject.createdAt).toLocaleDateString(i18next.language) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">
                          {subject.updatedAt ? new Date(subject.updatedAt).toLocaleDateString(i18next.language) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(subject)}
                          title={String(i18next.t('edit'))}
                        >
                          <EditIcon />
                        </IconButton>
                        {subject.isActive && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(subject)}
                            title={String(i18next.t('deactivate'))}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                {subjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18next.t('contact-subjects-empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog for creating/editing subjects */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState.editingSubject ? i18next.t('contact-subjects-edit') : i18next.t('contact-subjects-create')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label={String(i18next.t('contact-subjects-name-required'))}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              placeholder={String(i18next.t('contact-subjects-name-placeholder'))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={String(i18next.t('active'))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{i18next.t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {i18next.t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactSubjectsTab;